import { ref, computed, watch } from "vue";

export const themeModes = ["light", "dark", "system", "yd", "yd-dark"] as const;
export type ThemeMode = (typeof themeModes)[number];
export type ResolvedTheme = Exclude<ThemeMode, "system">;

export const THEME_STORAGE_KEY = "theme";
const DARK_CLASS = "dark";

// Global theme state
const theme = ref<ThemeMode>("dark");

export function useTheme() {
  const getStoredTheme = (): ThemeMode | null => {
    if (typeof window === "undefined") return null;
    try {
      const stored = localStorage.getItem(
        THEME_STORAGE_KEY,
      ) as ThemeMode | null;
      if (stored && themeModes.includes(stored)) {
        return stored as ThemeMode;
      }
    } catch {}
    return null;
  };

  const storeTheme = (newTheme: ThemeMode): void => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch {}
  };

  const resolvedTheme = computed<ResolvedTheme>(() => {
    if (theme.value === "system") {
      return "dark";
    }

    return theme.value;
  });

  const isDark = computed(() => resolvedTheme.value === "dark");

  const applyTheme = (resolved: ResolvedTheme): void => {
    if (typeof document === "undefined") return;

    // Remove all known theme classes
    document.documentElement.classList.remove(DARK_CLASS, "yd");

    if (resolved === "yd") {
      document.documentElement.classList.add("yd");
    } else if (resolved === "yd-dark") {
      document.documentElement.classList.add("yd", DARK_CLASS);
    } else if (resolved === "dark") {
      document.documentElement.classList.add(DARK_CLASS);
    }
  };

  const setTheme = (newTheme: ThemeMode): void => {
    theme.value = newTheme;
    storeTheme(newTheme);
  };

  const toggleTheme = (): void => {
    const current = resolvedTheme.value;
    setTheme(current === "dark" ? "light" : "dark");
  };

  const initTheme = (): void => {
    const storedTheme = getStoredTheme();
    theme.value = storedTheme || "dark";
  };

  watch(
    resolvedTheme,
    (newResolvedTheme) => {
      applyTheme(newResolvedTheme);
    },
    { immediate: true },
  );

  initTheme();

  return {
    theme,
    resolvedTheme,
    isDark,
    setTheme,
    toggleTheme,
  };
}
